/**
 * Die Verbindung als React-Haken.
 *
 * Setzt die beiden geprüften Teile zusammen — Lebenszyklus (`connection.ts`) und Spiegelung
 * (`view.ts`) — und fügt nur das hinzu, was React betrifft: aufbauen beim Einhängen, schließen
 * beim Abbauen, neu verbinden, wenn sich der Token ändert.
 *
 * **Ohne Token wird nicht verbunden.** Wer keinen hat, ist kein Spieler dieser Partie; ein
 * Verbindungsversuch wäre ein Socket, der nur abgewiesen werden kann.
 *
 * **Beim Abbauen wird geschlossen.** Sonst sammelt ein Browser mit jedem Bildschirmwechsel eine
 * offene Verbindung mehr — und der Server zählt sie als anwesende Spieler (§48).
 *
 * **Ein abgewiesener Token wird vergessen.** Sonst versucht die Anwendung es bei jedem Neuladen
 * wieder, und der Spieler sieht immer denselben Fehler, ohne einen Weg heraus. Ein
 * *gewöhnlicher* Abbruch löscht dagegen nichts — ein Netzausfall darf niemanden aus seiner
 * Partie werfen (§46).
 */

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import type { ClientMessage } from '@flaghex/contracts';

import { clearSession } from '../session.js';
import {
  createConnection,
  type Connection,
  type ConnectionStatus,
  type SocketLike,
} from './connection.js';
import { EMPTY_VIEW, applyMessage, dismissError, type GameView } from './view.js';

/** Was ein Test ersetzen darf. In Produktion bleibt alles beim Default. */
export interface UseConnectionOptions {
  readonly url?: string;
  readonly openSocket?: (url: string) => SocketLike;
  readonly schedule?: (run: () => void, delayMs: number) => () => void;
}

export interface GameConnection {
  readonly view: GameView;
  readonly status: ConnectionStatus;
  /** Gibt die Kennung der Absicht zurück, oder `undefined`, wenn nichts offen ist. */
  readonly send: (message: ClientMessage) => string | undefined;
  readonly dismissError: () => void;
}

type ViewAction = { readonly kind: 'message'; readonly message: Parameters<typeof applyMessage>[1] } | { readonly kind: 'dismiss' } | { readonly kind: 'reset' };

function reduce(view: GameView, action: ViewAction): GameView {
  switch (action.kind) {
    case 'message':
      return applyMessage(view, action.message);
    case 'dismiss':
      return dismissError(view);
    case 'reset':
      return EMPTY_VIEW;
  }
}

export function useConnection(
  sessionToken: string | undefined,
  options: UseConnectionOptions = {},
): GameConnection {
  const [view, dispatch] = useReducer(reduce, EMPTY_VIEW);
  const [status, setStatus] = useState<ConnectionStatus>('closed');
  const connection = useRef<Connection | undefined>(undefined);

  // Die Optionen sind in Produktion konstant; sie in die Abhängigkeiten zu schreiben würde bei
  // jedem Zeichnen neu verbinden — und eine Partie bei jedem Neuzeichnen kurz trennen.
  const settings = useRef(options);

  useEffect(() => {
    if (sessionToken === undefined) {
      return undefined;
    }
    dispatch({ kind: 'reset' });
    const current = createConnection({
      sessionToken,
      ...(settings.current.url === undefined ? {} : { url: settings.current.url }),
      ...(settings.current.openSocket === undefined
        ? {}
        : { openSocket: settings.current.openSocket }),
      ...(settings.current.schedule === undefined ? {} : { schedule: settings.current.schedule }),
      onMessage: (message) => dispatch({ kind: 'message', message }),
      onStatus: (next) => {
        setStatus(next);
        if (next === 'unauthenticated') {
          // Siehe Modulkopf: ein ungültiger Token wird durch Aufbewahren nicht besser.
          clearSession();
        }
      },
    });
    connection.current = current;

    return () => {
      current.close();
      connection.current = undefined;
    };
  }, [sessionToken]);

  const send = useCallback(
    (message: ClientMessage) => connection.current?.send(message),
    [],
  );
  const dismiss = useCallback(() => dispatch({ kind: 'dismiss' }), []);

  return useMemo(
    () => ({ view, status, send, dismissError: dismiss }),
    [view, status, send, dismiss],
  );
}
